// ST39 — in-memory ring-buffer time-series for the live dashboards.
//
// Metrics stream in live (client.metrics / queue.changed frames) but were only
// ever rendered as instantaneous gauges. This holds a bounded, downsampled
// history per metric key so the views can draw sparklines (util/VRAM/CPU/RAM per
// node + queue depth per tool) WITHOUT any chart dependency.
//
// The buffer is:
//   • RING-bounded  — at most `capacity` buckets per key; the oldest is evicted.
//   • DOWNSAMPLED   — samples that land in the same `bucketMs` time bucket collapse
//                     into ONE point (keeping last + min/max/avg), so a 2Hz
//                     heartbeat over 30min is ~360 points, not thousands.
//   • WINDOWED      — points older than `capacity * bucketMs` (the window) are dropped.
//
// Pure + framework-agnostic (unit-tested in timeseries.test.ts). The store owns one
// SeriesStore instance and records into it on each relevant frame.

export interface Bucket {
  /** Bucket index = floor(ts / bucketMs). */
  b: number;
  /** Representative timestamp (first sample's ts in the bucket). */
  ts: number;
  last: number;
  min: number;
  max: number;
  sum: number;
  count: number;
}

export interface Point {
  ts: number;
  value: number;
}

export type Agg = 'last' | 'avg' | 'min' | 'max';

function bucketValue(b: Bucket, agg: Agg): number {
  switch (agg) {
    case 'avg':
      return b.count ? b.sum / b.count : b.last;
    case 'min':
      return b.min;
    case 'max':
      return b.max;
    default:
      return b.last;
  }
}

export interface SeriesOptions {
  /** Bucket width in ms — samples within one bucket are downsampled to a point. */
  bucketMs?: number;
  /** Max buckets retained per key (the ring). window = capacity * bucketMs. */
  capacity?: number;
}

export class SeriesStore {
  readonly bucketMs: number;
  readonly capacity: number;
  private map = new Map<string, Bucket[]>();

  constructor(opts: SeriesOptions = {}) {
    // Default: a 5s bucket × 360 buckets = a 30-minute window (inside the 15–60min band).
    this.bucketMs = Math.max(1, opts.bucketMs ?? 5000);
    this.capacity = Math.max(1, opts.capacity ?? 360);
  }

  /** The window covered, in ms. */
  get windowMs(): number {
    return this.bucketMs * this.capacity;
  }

  /** Record a sample. Downsamples into the current bucket, rings + windows the rest. */
  record(key: string, ts: number, value: number): void {
    if (!Number.isFinite(value)) return;
    const b = Math.floor(ts / this.bucketMs);
    let arr = this.map.get(key);
    if (!arr) {
      arr = [];
      this.map.set(key, arr);
    }
    const tail = arr[arr.length - 1];
    if (tail && tail.b === b) {
      // Same bucket — downsample (aggregate) rather than append.
      tail.last = value;
      tail.min = Math.min(tail.min, value);
      tail.max = Math.max(tail.max, value);
      tail.sum += value;
      tail.count += 1;
    } else if (tail && b < tail.b) {
      // Out-of-order older sample — ignore (live feed is monotonic in practice).
      return;
    } else {
      arr.push({ b, ts, last: value, min: value, max: value, sum: value, count: 1 });
    }
    this.prune(arr, b);
  }

  private prune(arr: Bucket[], newestBucket: number): void {
    // Window: drop buckets older than `capacity` buckets behind the newest.
    const minBucket = newestBucket - this.capacity + 1;
    let drop = 0;
    while (drop < arr.length && arr[drop].b < minBucket) drop += 1;
    // Ring: also cap the absolute count.
    if (arr.length - drop > this.capacity) drop = arr.length - this.capacity;
    if (drop > 0) arr.splice(0, drop);
  }

  /** Points for a key, oldest→newest. `sinceMs` restricts to a recent sub-window. */
  points(key: string, agg: Agg = 'last', sinceMs?: number): Point[] {
    const arr = this.map.get(key);
    if (!arr || arr.length === 0) return [];
    const cutoff = sinceMs != null ? arr[arr.length - 1].ts - sinceMs : -Infinity;
    const out: Point[] = [];
    for (const b of arr) {
      if (b.ts >= cutoff) out.push({ ts: b.ts, value: bucketValue(b, agg) });
    }
    return out;
  }

  /** Latest value for a key (or undefined). */
  latest(key: string, agg: Agg = 'last'): number | undefined {
    const arr = this.map.get(key);
    if (!arr || arr.length === 0) return undefined;
    return bucketValue(arr[arr.length - 1], agg);
  }

  /** Number of retained buckets for a key (for tests/diagnostics). */
  size(key: string): number {
    return this.map.get(key)?.length ?? 0;
  }

  keys(): string[] {
    return [...this.map.keys()];
  }

  clear(): void {
    this.map.clear();
  }
}
